import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { IconButton, Tooltip, Popover } from '@material-ui/core';
import PlaceIcon from '@material-ui/icons/Place';
import TimelineIcon from '@material-ui/icons/Timeline';
import CropSquareIcon from '@material-ui/icons/CropSquare';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import CloseIcon from '@material-ui/icons/Close';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CheckIcon from '@material-ui/icons/Check';
import ReplayIcon from '@material-ui/icons/Replay';
import ImageSearchIcon from '@material-ui/icons/ImageSearch';
import FindReplaceIcon from '@material-ui/icons/FindReplace';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';

const useStyles = makeStyles(() => ({
  card: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid #e0e0e0',
    background: '#ffffff',
    cursor: 'pointer',
    marginBottom: 6,
    transition: 'all 0.15s ease',
    '&:hover': { borderColor: '#bdbdbd', background: '#fafafa' },
  },
  cardActive: {
    borderColor: '#4F46E5',
    background: '#EEF2FF',
    '&:hover': { borderColor: '#4F46E5', background: '#E0E7FF' },
  },
  cardPlaced: {
    borderColor: '#a5d6a7',
    background: '#f1f8f1',
    '&:hover': { borderColor: '#81c784', background: '#e8f5e9' },
  },
  cardPartial: {
    borderColor: '#ffe082',
    background: '#fffde7',
    '&:hover': { borderColor: '#ffd54f', background: '#fff9c4' },
  },
  cardCvPending: {
    borderColor: '#7c3aed',
    background: '#f5f3ff',
    '&:hover': { borderColor: '#6d28d9', background: '#ede9fe' },
  },
  cardSkipped: {
    borderColor: '#e0e0e0',
    background: '#fafafa',
    opacity: 0.6,
    '&:hover': { borderColor: '#bdbdbd', background: '#f5f5f5' },
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    flexShrink: 0,
    marginTop: 5,
  },
  symbolThumb: {
    width: 28,
    height: 28,
    borderRadius: 4,
    border: '1px solid #e0e0e0',
    objectFit: 'contain',
    background: '#fff',
    flexShrink: 0,
    cursor: 'pointer',
  },
  body: {
    flex: 1,
    minWidth: 0,
  },
  description: {
    fontSize: 13,
    fontWeight: 500,
    color: '#333',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  descriptionSkipped: {
    textDecoration: 'line-through',
    color: '#999',
  },
  meta: {
    fontSize: 11,
    color: '#888',
    marginTop: 2,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    flexShrink: 0,
  },
  iconBtn: { padding: 4 },
  placedBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: 3,
    fontSize: 10,
    fontWeight: 600,
    color: '#388e3c',
    padding: '2px 6px',
    background: '#e8f5e9',
    borderRadius: 10,
    marginTop: 4,
  },
  partialBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: 3,
    fontSize: 10,
    fontWeight: 600,
    color: '#f57f17',
    padding: '2px 6px',
    background: '#fff9c4',
    borderRadius: 10,
    marginTop: 4,
  },
  cvPendingBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: 3,
    fontSize: 10,
    fontWeight: 600,
    color: '#7c3aed',
    padding: '2px 6px',
    background: '#ede9fe',
    borderRadius: 10,
    marginTop: 4,
  },
  popoverImg: {
    maxWidth: 400,
    maxHeight: 300,
    display: 'block',
  },
  placementList: {
    borderTop: '1px solid #e0e0e0',
    padding: '4px 0',
  },
  placementRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '2px 12px',
    cursor: 'pointer',
    '&:hover': { background: '#f5f5f5' },
    fontSize: 12,
  },
  placementRowActive: {
    background: '#e3f2fd',
    '&:hover': { background: '#bbdefb' },
  },
  placementLabel: {
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    color: '#555',
  },
  placementActions: {
    display: 'flex',
    gap: 2,
  },
  placementTotal: {
    padding: '4px 12px',
    fontSize: 12,
    fontWeight: 600,
    color: '#333',
    borderTop: '1px solid #eee',
  },
  addPlacement: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    padding: '6px 12px',
    fontSize: 12,
    color: '#1976d2',
    cursor: 'pointer',
    '&:hover': { background: '#e3f2fd' },
  },
}));

const GEOMETRY_ICONS = {
  point: PlaceIcon,
  polyline: TimelineIcon,
  polygon: CropSquareIcon,
};

const GEOMETRY_LABELS = {
  point: 'Point',
  polyline: 'Polyline',
  polygon: 'Polygon',
};

/**
 * PlacementItemCard — shows a parsed item with optional symbol crop thumbnail.
 *
 * Props:
 *   item, isActive, onSelect, onDraw, onSkip, onChangeType
 *   cropBaseUrl  — if set, shows a small thumbnail of the row's source crop
 *   onPickSymbol — (itemId) => void — opens the symbol picker modal
 */
const PlacementItemCard = ({
  item, isActive, isExpanded, activePlacementId,
  onSelect, onToggleExpand,
  onSkip, onChangeType, cropBaseUrl, onPickSymbol, onFindOnPage,
  onEditPlacement, onDeletePlacement, onAddPlacement,
  onConfirmEdit, onCancelEdit,
  editingPlacement,
}) => {
  const classes = useStyles();
  const [popoverAnchor, setPopoverAnchor] = useState(null);

  const { itemId, description, section, parsedQty, parsedUnit, status, geometryType, color, sourceRef } = item;
  const placements = item.placements || [];
  const placementCount = placements.length;

  const TYPES = ['point', 'polyline', 'polygon'];
  const handleTypeClick = (e) => {
    e.stopPropagation();
    if (!onChangeType) return;
    const currentIdx = TYPES.indexOf(geometryType);
    const nextType = TYPES[(currentIdx + 1) % TYPES.length];
    onChangeType(itemId, nextType);
  };

  const GeomIcon = geometryType ? GEOMETRY_ICONS[geometryType] : HelpOutlineIcon;
  const geomLabel = geometryType ? GEOMETRY_LABELS[geometryType] : 'Type?';

  let cardClass = classes.card;
  if (isActive) cardClass += ` ${classes.cardActive}`;
  else if (status === 'placed') cardClass += ` ${classes.cardPlaced}`;
  else if (status === 'partial') cardClass += ` ${classes.cardPartial}`;
  else if (status === 'cv_pending') cardClass += ` ${classes.cardCvPending}`;
  else if (status === 'skipped') cardClass += ` ${classes.cardSkipped}`;

  const metaParts = [section];
  if (parsedQty > 0) metaParts.push(`Qty: ${parsedQty} ${parsedUnit || ''}`.trim());

  const isPointItem = geometryType === 'point' || (parsedUnit || '').toUpperCase() === 'EA';
  const totalMeasured = placements.reduce((sum, p) => sum + (p.measuredQty || 0), 0);
  const progressText = isPointItem
    ? `${placementCount}/${parsedQty || '?'}`
    : parsedQty > 0 ? `${Math.round(totalMeasured)}/${parsedQty} ${(parsedUnit || '').toUpperCase()}` : '';
  const showPlacedBadge = status === 'placed' && parsedQty > 0;
  const showPartialBadge = status === 'partial' && parsedQty > 0;

  // Symbol thumbnail: prefer user-cropped symbolDataUrl, fall back to crop-preview endpoint
  const symbolUrl = item.symbolDataUrl || null;
  const cropUrl = (!symbolUrl && cropBaseUrl && sourceRef && sourceRef.sheet != null && sourceRef.table != null)
    ? `${cropBaseUrl}/sheets/${sourceRef.sheet}/tables/${sourceRef.table}/crop-preview?artifactIndex=0&pad=12&rowIndex=${sourceRef.row}`
    : null;
  const thumbUrl = symbolUrl || cropUrl;

  return (
    <>
    <div className={cardClass} onClick={() => onToggleExpand && onToggleExpand(itemId)}>
      {/* Symbol thumbnail or colored dot */}
      {thumbUrl ? (
        <Tooltip title={symbolUrl ? 'Custom symbol — click to change' : 'Click to pick symbol from legend'}>
          <img
            src={thumbUrl}
            alt=""
            className={classes.symbolThumb}
            onClick={(e) => {
              e.stopPropagation();
              if (onPickSymbol) onPickSymbol(itemId);
              else setPopoverAnchor(e.currentTarget);
            }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </Tooltip>
      ) : (
        <Tooltip title="Click to pick symbol from legend">
          <div
            className={classes.dot}
            style={{ background: color, cursor: 'pointer' }}
            onClick={(e) => { e.stopPropagation(); if (onPickSymbol) onPickSymbol(itemId); }}
          />
        </Tooltip>
      )}

      <div className={classes.body}>
        <div
          className={`${classes.description} ${status === 'skipped' ? classes.descriptionSkipped : ''}`}
          title={description}
        >
          {description}
          {item.size ? ` — ${item.size}` : ''}
        </div>
        <div className={classes.meta}>
          {metaParts.join(' · ')}
        </div>
        {showPlacedBadge && (
          <div className={classes.placedBadge}>
            <CheckCircleIcon style={{ fontSize: 10 }} />
            {progressText} placed
          </div>
        )}
        {showPartialBadge && (
          <div className={classes.partialBadge}>
            {progressText} placed
          </div>
        )}
        {status === 'cv_pending' && item.matchRun?.candidates?.length > 0 && (
          <div className={classes.cvPendingBadge}>
            {item.matchRun.candidates.filter(c => c.status === 'pending').length} candidates
          </div>
        )}
      </div>

      <div className={classes.actions}>
        {/* Pick/view symbol */}
        {onPickSymbol && (
          <Tooltip title={symbolUrl ? 'Change symbol' : 'Pick symbol from legend'}>
            <IconButton
              size="small"
              className={classes.iconBtn}
              onClick={(e) => { e.stopPropagation(); onPickSymbol(itemId); }}
            >
              <ImageSearchIcon style={{ fontSize: 15, color: symbolUrl ? '#4caf50' : '#1565c0' }} />
            </IconButton>
          </Tooltip>
        )}

        {/* Find on page (CV match) */}
        {onFindOnPage && item.symbolDataUrl && item.geometryType === 'point' && (
          <Tooltip title="Find on page (CV match)">
            <IconButton
              size="small"
              className={classes.iconBtn}
              onClick={(e) => { e.stopPropagation(); onFindOnPage(itemId); }}
            >
              <FindReplaceIcon style={{ fontSize: 15, color: '#7c3aed' }} />
            </IconButton>
          </Tooltip>
        )}

        {/* Geometry type toggle */}
        <Tooltip title={`Geometry: ${geomLabel} — click to cycle`}>
          <IconButton size="small" className={classes.iconBtn} onClick={handleTypeClick}>
            <GeomIcon style={{ fontSize: 15, color: geometryType ? '#555' : '#bbb' }} />
          </IconButton>
        </Tooltip>

        {/* Reset button — only for placed/skipped */}
        {(status === 'placed' || status === 'skipped') && (
          <Tooltip title="Reset to queued">
            <IconButton size="small" className={classes.iconBtn} onClick={(e) => { e.stopPropagation(); if (onSelect) onSelect(itemId); }}>
              <ReplayIcon style={{ fontSize: 15, color: '#999' }} />
            </IconButton>
          </Tooltip>
        )}

        {/* Skip button */}
        {(status === 'queued' || status === 'partial') && (
          <Tooltip title="Skip this item">
            <IconButton size="small" className={classes.iconBtn} onClick={(e) => { e.stopPropagation(); if (onSkip) onSkip(itemId); }}>
              <CloseIcon style={{ fontSize: 14, color: '#bbb' }} />
            </IconButton>
          </Tooltip>
        )}
      </div>

      {/* Enlarged symbol popover */}
      <Popover
        open={Boolean(popoverAnchor)}
        anchorEl={popoverAnchor}
        onClose={() => setPopoverAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        {cropUrl && (
          <img
            src={cropUrl}
            alt={description}
            className={classes.popoverImg}
            style={{ padding: 8 }}
          />
        )}
      </Popover>
    </div>

    {/* Expandable placement sub-list — outside the flex card */}
    {isExpanded && (
      <div className={classes.placementList}>
        {placements.map((placement, idx) => {
            const isEditingThis = editingPlacement &&
              editingPlacement.itemId === itemId &&
              editingPlacement.placementId === placement.id;
            const isActivePlacement = activePlacementId === placement.id;
            const label = isPointItem
              ? `Marker ${idx + 1}`
              : `Area ${idx + 1}`;
            const measurement = !isPointItem && placement.measuredQty
              ? ` — ${Math.round(placement.measuredQty)} ${(placement.measuredUnit || '').toUpperCase()}`
              : '';
            const pageLabel = ` (Pg ${(placement.page || 0) + 1})`;

            return (
              <div
                key={placement.id}
                className={`${classes.placementRow} ${isActivePlacement ? classes.placementRowActive : ''}`}
                onClick={() => onSelect && onSelect(itemId, placement.id)}
              >
                <span className={classes.placementLabel}>
                  {label}{measurement}{pageLabel}
                </span>
                <span className={classes.placementActions}>
                  {isEditingThis ? (
                    <>
                      <IconButton size="small" style={{ padding: 2 }} onClick={(e) => { e.stopPropagation(); onConfirmEdit && onConfirmEdit(); }}>
                        <CheckIcon fontSize="small" style={{ fontSize: 14, color: '#4caf50' }} />
                      </IconButton>
                      <IconButton size="small" style={{ padding: 2 }} onClick={(e) => { e.stopPropagation(); onCancelEdit && onCancelEdit(); }}>
                        <CloseIcon fontSize="small" style={{ fontSize: 14, color: '#f44336' }} />
                      </IconButton>
                    </>
                  ) : (
                    <>
                      <IconButton size="small" style={{ padding: 2 }} onClick={(e) => {
                        e.stopPropagation();
                        onEditPlacement && onEditPlacement(itemId, placement.id);
                      }}>
                        <EditIcon style={{ fontSize: 14 }} />
                      </IconButton>
                      <IconButton size="small" style={{ padding: 2 }} onClick={(e) => {
                        e.stopPropagation();
                        onDeletePlacement && onDeletePlacement(itemId, placement.id);
                      }}>
                        <DeleteIcon style={{ fontSize: 14, color: '#e57373' }} />
                      </IconButton>
                    </>
                  )}
                </span>
              </div>
            );
          })}

          {/* Running total for polygon/polyline items */}
          {!isPointItem && placementCount > 0 && (
            <div className={classes.placementTotal}>
              Total: {Math.round(totalMeasured)} {(parsedUnit || '').toUpperCase()}
            </div>
          )}

          {/* Add Placement button — always visible */}
          <div
            className={classes.addPlacement}
            onClick={(e) => { e.stopPropagation(); onAddPlacement && onAddPlacement(itemId); }}
          >
            <AddIcon style={{ fontSize: 16 }} /> Add Placement
          </div>
        </div>
      )}
    </>
  );
};

export default PlacementItemCard;
